import { CHANNELS, NotificationEventTypes, NotificationEventTypeVariables, NotificationTemplate, RECIPIENT_TYPES } from 'app/types';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Card } from 'react-bootstrap-v5';
import { CardBody } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import DateTimeConfigComponent from './DateTimeConfigComponent';
import { QuickVariables } from './QuickVariables';

interface NotificationTemplateEditorProps {
  template?: NotificationTemplate | null;
  onSave: (data: NotificationTemplate) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

export const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  template,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(!!template?.sendRelativeTo);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);

  // Enhanced validation schema with better error messages
  const validationSchema = Yup.object({
    templateId: Yup.string()
      .required('Template ID is required')
      .matches(/^[a-zA-Z0-9_-]+$/, 'Template ID can only contain letters, numbers, underscores and hyphens')
      .min(3, 'Template ID must be at least 3 characters')
      .max(50, 'Template ID must be less than 50 characters')
      .test('no-spaces', 'Template ID cannot contain spaces', (value) => !value?.includes(' ')),
    eventType: Yup.string()
      .required('Event type is required')
      .oneOf(Object.keys(NotificationEventTypes), 'Please select a valid event type'),
    recipientType: Yup.string()
      .required('Recipient type is required')
      .oneOf(Array.from(RECIPIENT_TYPES), 'Please select a valid recipient type'),
    body: Yup.string()
      .required('Message body is required')
      .min(10, 'Message body must be at least 10 characters')
      .max(50000, 'Message body is too long (max 50,000 characters)')
      .test('not-empty-html', 'Message body cannot be empty', (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
      }),
    subject: Yup.string().when('channel', {
      is: 'email',
      then: (schema) => schema
        .required('Subject is required for email notifications')
        .min(3, 'Subject must be at least 3 characters')
        .max(200, 'Subject must be less than 200 characters')
        .test('not-only-whitespace', 'Subject cannot be only whitespace', (value) =>
          value ? value.trim().length > 0 : true
        ),
    }),
    channel: Yup.string()
      .required('Channel is required')
      .oneOf(Array.from(CHANNELS), 'Please select a valid channel'),
  });

  const formik = useFormik({
    initialValues: {
      enabled: true,
      tags: [],
      metadata: {},
      templateId: '',
      body: '',
      eventType: '',
      channel: 'email' as string,
      recipientType: '',
      subject: '',
      sendRelativeTo: null,
      ...template
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, setFieldError, setStatus }) => {
      try {
        setStatus(null);
        const now = new Date().toISOString();
        const payload: NotificationTemplate = {
          ...values,
          templateId: values.templateId.trim(),
          subject: values.subject?.trim(),
          createdAt: template?.createdAt || now,
          updatedAt: now,
          enabled: Boolean(values.enabled),
          sendRelativeTo: scheduleEnabled ? values.sendRelativeTo : null,
        } as NotificationTemplate;

        await onSave(payload);
      } catch (error: any) {
        console.error('Error saving template:', error);

        // Enhanced error handling with user-friendly messages
        const errorMessages: Record<string, { field: string; message: string }> = {
          'TEMPLATE_ID_EXISTS': { field: 'templateId', message: 'This template ID is already in use. Please choose a different one.' },
          'INVALID_EVENT_TYPE': { field: 'eventType', message: 'The selected event type is not valid.' },
          'INVALID_RECIPIENT': { field: 'recipientType', message: 'The selected recipient type is not valid.' },
          'INVALID_TEMPLATE_FORMAT': { field: 'body', message: 'The template format is invalid. Please check your content.' },
          'NETWORK_ERROR': { field: 'body', message: 'Network error. Please check your connection and try again.' },
          'PERMISSION_DENIED': { field: 'body', message: 'You do not have permission to save this template.' },
        };

        const errorInfo = errorMessages[error.code] || {
          field: 'body',
          message: error.message || 'An unexpected error occurred. Please try again.'
        };

        setFieldError(errorInfo.field, errorInfo.message);
        setStatus({ type: 'error', message: errorInfo.message });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Add this effect to handle schedule toggle
  useEffect(() => {
    if (!scheduleEnabled) {
      formik.setFieldValue('sendRelativeTo', null);
    }
  }, [scheduleEnabled]);

  useEffect(() => {
    let tooltipInstance: any;
    if (tooltipRef.current && (window as any).bootstrap?.Tooltip) {
      tooltipInstance = new (window as any).bootstrap.Tooltip(tooltipRef.current);
    }
    return () => {
      tooltipInstance?.dispose();
    };
  }, [formik.values.eventType]);

  // HTML edit mode state
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [htmlValue, setHtmlValue] = useState(formik.values.body || '');


  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: formik.values.body || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      formik.setFieldValue('body', html);
    },
    onFocus: () => {
      // Clear any previous body errors when editor gains focus
      if (formik.errors.body) {
        formik.setFieldError('body', '');
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-3',
      },
    },
  });


  // Handle editor container click to focus editor
  const handleEditorContainerClick = useCallback((event: React.MouseEvent) => {
    if (editor && !editor.isFocused) {
      event.preventDefault();
      editor.commands.focus();
    }
  }, [editor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  // Auto-save draft functionality (optional)
  useEffect(() => {
    if (!template && formik.values.templateId) {
      const timer = setTimeout(() => {
        // Auto-save logic here
        localStorage.setItem(`draft_${formik.values.templateId}`, JSON.stringify(formik.values));
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [formik.values, template]);

  // Sync htmlValue with editor content
  useEffect(() => {
    if (!htmlEditMode && editor) {
      setHtmlValue(editor.getHTML());
    }
  }, [htmlEditMode, editor]);

  // When switching from HTML mode to WYSIWYG, update editor content
  const handleHtmlSave = () => {
    if (editor) {
      editor.commands.setContent(htmlValue);
      formik.setFieldValue('body', htmlValue);
    }
    setHtmlEditMode(false);
  };

  // For regular textarea (non-email channels):
  const handleVariableInsert = useCallback((variable: string) => {
    const textarea = document.querySelector('textarea[name="body"]') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = formik.values.body || '';

      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      formik.setFieldValue('body', newValue);

      // Restore cursor position
      setTimeout(() => {
        textarea.setSelectionRange(start + variable.length, start + variable.length);
        textarea.focus();
      }, 0);
    }
  }, [formik]);

  // For TipTap editor (email channel):
  const handleEditorVariableInsert = useCallback((variable: string) => {
    if (editor) {
      editor.chain().focus().insertContent(variable).run();
    }
  }, [editor]);


  const renderToolbar = () => (
    <div className="border-bottom p-2 bg-light d-flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleBold().run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('bold') ? 'active' : ''}`}
        title="Bold"
        disabled={!editor}
      >
        <i className="bi bi-type-bold"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleItalic().run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('italic') ? 'active' : ''}`}
        title="Italic"
        disabled={!editor}
      >
        <i className="bi bi-type-italic"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('underline') ? 'active' : ''}`}
        title="Underline"
        disabled={!editor}
      >
        <i className="bi bi-type-underline"></i>
      </button>

      <div className="vr mx-1"></div>

      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('heading', { level: 1 }) ? 'active' : ''}`}
        title="Heading 1"
        disabled={!editor}
      >
        <i className="bi bi-type-h1"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('heading', { level: 2 }) ? 'active' : ''}`}
        title="Heading 2"
        disabled={!editor}
      >
        <i className="bi bi-type-h2"></i>
      </button>

      <div className="vr mx-1"></div>

      <button
        type="button"
        onClick={() => editor?.chain().focus().setTextAlign('left').run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
        title="Align Left"
        disabled={!editor}
      >
        <i className="bi bi-text-left"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().setTextAlign('center').run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
        title="Align Center"
        disabled={!editor}
      >
        <i className="bi bi-text-center"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().setTextAlign('right').run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
        title="Align Right"
        disabled={!editor}
      >
        <i className="bi bi-text-right"></i>
      </button>

      <div className="vr mx-1"></div>

      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('bulletList') ? 'active' : ''}`}
        title="Bullet List"
        disabled={!editor}
      >
        <i className="bi bi-list-ul"></i>
      </button>
      <button
        type="button"
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('orderedList') ? 'active' : ''}`}
        title="Numbered List"
        disabled={!editor}
      >
        <i className="bi bi-list-ol"></i>
      </button>

      <div className="vr mx-1"></div>

      <button
        type="button"
        onClick={() => {
          const url = window.prompt('Enter link URL:');
          if (url) {
            editor?.chain().focus().setLink({ href: url }).run();
          }
        }}
        className={`btn btn-sm btn-outline-secondary ${editor?.isActive('link') ? 'active' : ''}`}
        title="Insert Link"
        disabled={!editor}
      >
        <i className="bi bi-link-45deg"></i>
      </button>
    </div>
  );

  return (
    <Card className="shadow-sm">
      <CardBody className="p-4">
        {/* Status Messages */}
        {formik.status && (
          <div className={`alert ${formik.status.type === 'error' ? 'alert-danger' : 'alert-success'} mb-4`}>
            <i className={`bi bi-${formik.status.type === 'error' ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
            {formik.status.message}
          </div>
        )}

        <form onSubmit={formik.handleSubmit} className="needs-validation" noValidate>
          <div className="row">
            {/* Left Column */}
            <div className="col-md-6">
              {/* Template ID */}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  <i className="bi bi-tag me-1"></i>
                  Template ID
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.templateId && formik.errors.templateId ? 'is-invalid' : formik.touched.templateId && !formik.errors.templateId ? 'is-valid' : ''}`}
                  placeholder="e.g., welcome_email_v1"
                  {...formik.getFieldProps('templateId')}
                  disabled={!!template} // Disable editing existing template ID
                />
                {formik.touched.templateId && formik.errors.templateId && (
                  <div className="invalid-feedback">{formik.errors.templateId}</div>
                )}
                {!template && (
                  <div className="form-text">
                    Use lowercase letters, numbers, underscores, and hyphens only
                  </div>
                )}
              </div>

              {/* Event Type */}
              <div className="mb-4">
                <label className="form-label fw-semibold d-flex align-items-center">
                  <i className="bi bi-lightning me-1"></i>
                  Event Type
                  <span className="text-danger ms-1">*</span>
                  {formik.values.eventType && (
                    <i
                      ref={tooltipRef}
                      className="bi bi-question-circle ms-2 text-muted"
                      style={{ cursor: 'help' }}
                      title={`Available variables: ${NotificationEventTypeVariables[formik.values.eventType as keyof typeof NotificationEventTypes]?.map(v => `{{${v}}}`).join(', ')}`}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                    ></i>
                  )}
                </label>
                <select
                  className={`form-select ${formik.touched.eventType && formik.errors.eventType ? 'is-invalid' : formik.touched.eventType && !formik.errors.eventType ? 'is-valid' : ''}`}
                  {...formik.getFieldProps('eventType')}
                >
                  <option value="">-- Select Event Type --</option>
                  {Object.keys(NotificationEventTypes).map(eventType => (
                    <option key={eventType} value={eventType}>
                      {eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
                {formik.touched.eventType && formik.errors.eventType && (
                  <div className="invalid-feedback">{formik.errors.eventType}</div>
                )}
              </div>
              {/* Template Status */}
              <div className="mb-4">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="enabled"
                    {...formik.getFieldProps('enabled')}
                    checked={formik.values.enabled}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="enabled">
                    Template Active
                  </label>
                </div>
                <div className="form-text">
                  {formik.values.enabled ? 'This template is active and will be used for notifications' : 'This template is disabled and will not be used'}
                </div>
              </div>

            </div>

            {/* Right Column */}
            <div className="col-md-6">

              {/* Channel */}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  <i className="bi bi-broadcast me-1"></i>
                  Channel
                  <span className="text-danger ms-1">*</span>
                </label>
                <select
                  className={`form-select ${formik.touched.channel && formik.errors.channel ? 'is-invalid' : formik.touched.channel && !formik.errors.channel ? 'is-valid' : ''}`}
                  {...formik.getFieldProps('channel')}
                >
                  {CHANNELS.map(channel => (
                    <option key={channel} value={channel}>
                      {channel.charAt(0).toUpperCase() + channel.slice(1)}
                    </option>
                  ))}
                </select>
                {formik.touched.channel && formik.errors.channel && (
                  <div className="invalid-feedback">{formik.errors.channel}</div>
                )}
              </div>

              {/* Recipient Type */}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  <i className="bi bi-people me-1"></i>
                  Recipient Type
                  <span className="text-danger ms-1">*</span>
                </label>
                <select
                  className={`form-select ${formik.touched.recipientType && formik.errors.recipientType ? 'is-invalid' : formik.touched.recipientType && !formik.errors.recipientType ? 'is-valid' : ''}`}
                  {...formik.getFieldProps('recipientType')}
                >
                  <option value="">-- Select Recipient Type --</option>
                  {RECIPIENT_TYPES.map(recipientType => (
                    <option key={recipientType} value={recipientType}>
                      {recipientType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
                {formik.touched.recipientType && formik.errors.recipientType && (
                  <div className="invalid-feedback">{formik.errors.recipientType}</div>
                )}
              </div>
            </div>
          </div>

          {/* Subject (Email only) */}
          {formik.values.channel === 'email' && (
            <div className="mb-4">
              <label className="form-label fw-semibold">
                <i className="bi bi-envelope me-1"></i>
                Subject Line
                <span className="text-danger ms-1">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formik.touched.subject && formik.errors.subject ? 'is-invalid' : formik.touched.subject && !formik.errors.subject ? 'is-valid' : ''}`}
                placeholder="Enter email subject"
                {...formik.getFieldProps('subject')}
              />
              {formik.touched.subject && formik.errors.subject && (
                <div className="invalid-feedback">{formik.errors.subject}</div>
              )}
              <div className="form-text">
                {formik.values.subject?.length || 0}/200 characters
              </div>
            </div>
          )}

          {/* Message Body */}
          <div className="mb-4">
            <label className="form-label fw-semibold">
              <i className="bi bi-card-text me-1"></i>
              Message Body
              <span className="text-danger ms-1">*</span>
            </label>
            {formik.values.eventType && (<QuickVariables
              eventType={formik.values.eventType}
              recipientType={formik.values.recipientType}
              onInsert={formik.values.channel === 'email' ? handleEditorVariableInsert : handleVariableInsert}
            />)}
            {formik.values.channel === 'email' ? (
              <div className="border rounded">
                {renderToolbar()}
                <div className="border rounded">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => setHtmlEditMode((v) => !v)}
                  >
                    <i className="bi bi-code-slash me-1"></i>
                    {/* {htmlEditMode ? 'WYSIWYG' : 'Edit HTML'} */}
                  </button>
                </div>
                {htmlEditMode ? (
                  <div className="p-2">
                    <textarea
                      className="form-control font-monospace"
                      rows={12}
                      value={htmlValue}
                      onChange={e => setHtmlValue(e.target.value)}
                      style={{ minHeight: '200px' }}
                    />
                    <div className="d-flex justify-content-end mt-2">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleHtmlSave}
                      >
                        Apply HTML
                      </button>
                      <button
                        type="button"
                        className="btn btn-light btn-sm ms-2"
                        onClick={() => setHtmlEditMode(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    ref={editorContainerRef}
                    className={`editor-container ${formik.touched.body && formik.errors.body ? 'border-danger' : ''}`}
                    onClick={handleEditorContainerClick}
                    style={{ minHeight: '250px', cursor: 'text' }}
                  >
                    <EditorContent editor={editor} />
                  </div>
                )}

              </div>
            ) : (
              <textarea
                className={`form-control ${formik.touched.body && formik.errors.body ? 'is-invalid' : formik.touched.body && !formik.errors.body ? 'is-valid' : ''}`}
                rows={8}
                placeholder={`Enter your ${formik.values.channel} message here...`}
                {...formik.getFieldProps('body')}
              />
            )}

            {formik.touched.body && formik.errors.body && (
              <div className="invalid-feedback d-block">{formik.errors.body}</div>
            )}

            <div className="form-text d-flex justify-content-between">
              <span>{formik.values.body?.replace(/<[^>]*>/g, '').length || 0}/50,000 characters</span>
            </div>
          </div>

          {/* Schedule Toggle & Schedule At (for reminders and scheduled notifications) */}
          <div className="mb-4">
            <div className="form-check form-switch mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="scheduleToggle"
                checked={scheduleEnabled}
                onChange={e => setScheduleEnabled(e.target.checked)}
              />
              <label className="form-check-label fw-semibold" htmlFor="scheduleToggle">
                Schedule Notification (send relative to event)
              </label>
            </div>
            {scheduleEnabled && (
              <DateTimeConfigComponent
                value={{
                  mode: formik.values.sendRelativeTo?.mode || 'direct',
                  offsetMinutes: typeof formik.values.sendRelativeTo?.offsetMinutes === 'number'
                    ? formik.values.sendRelativeTo.offsetMinutes
                    : Number(formik.values.sendRelativeTo?.offsetMinutes) || 0,
                  directPath: formik.values.sendRelativeTo?.directPath || '',
                  componentPaths: formik.values.sendRelativeTo?.componentPaths || {}
                }}
                onChange={config =>
                  formik.setFieldValue('sendRelativeTo', {
                    ...config
                  })
                }
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center pt-3 border-top">
            <div>
              {onCancel && (
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={onCancel}
                  disabled={formik.isSubmitting || isLoading}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Cancel
                </button>
              )}
            </div>

            <div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty || isLoading}
              >
                {formik.isSubmitting || isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    {template ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};